import React from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Skeleton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

interface ThumbnailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  thumbnailUrl: string | null;
  routeName: string;
}

export const ThumbnailPreviewModal: React.FC<ThumbnailPreviewModalProps> = ({
  open,
  onClose,
  thumbnailUrl,
  routeName
}) => {
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);

  const handleClose = () => {
    onClose();
  };

  // Reset states when modal opens with new thumbnail
  React.useEffect(() => {
    if (open && thumbnailUrl) {
      setImageLoaded(false);
      setImageError(false);
    }
  }, [open, thumbnailUrl]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

    // Keyboard support
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          margin: 0,
          maxHeight: '100vh',
          maxWidth: '100vw',
          overflow: 'hidden',
          boxShadow: 'none'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)'
        }
      }}
    >
      <DialogContent 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
        sx={{ 
          p: 0, 
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          '&:first-of-type': { pt: 0 }
        }}
      >


        {/* Route Name Overlay */}
        <Box
          sx={{
            position: 'fixed',
            top: 24,
            left: 24,
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            maxWidth: 'calc(100vw - 120px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: 'white',
              fontSize: '0.95rem',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
            }}
          >
            {routeName}
          </Typography>
        </Box>

        {/* Close Button */}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          sx={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Instructions */}
        <Typography
          variant="caption"
          sx={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            zIndex: 1000,
            color: 'rgba(255, 255, 255, 0.9)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: '0.75rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          Scroll/pinch to zoom • Drag to pan • Tap X to close
        </Typography>

        {/* Image Container with zoom/pan */}
        {open && thumbnailUrl && !imageError ? (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{ pointerEvents: 'auto' }}
            >
              <TransformWrapper
                initialScale={1}
                minScale={0.5}
                maxScale={4}
                wheel={{
                  step: 0.1
                }}
                panning={{
                  velocityDisabled: true
                }}
                centerOnInit={false}
                limitToBounds={false}
                initialPositionX={0}
                initialPositionY={0}
              >
              {() => (
                <TransformComponent
                  wrapperStyle={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  contentStyle={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}

                >
                {!imageLoaded && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{
                      position: 'absolute',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }}
                  />
                )}
                <img
                  src={thumbnailUrl}
                  alt={`${routeName} route preview`}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    opacity: imageLoaded ? 1 : 0,
                    userSelect: 'none',
                    transition: 'opacity 0.3s ease',
                    cursor: 'grab',
                    pointerEvents: 'none'
                  }}
                />
              </TransformComponent>
            )}
          </TransformWrapper>
            </Box>
          </Box>
        ) : open ? (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.7)',
              p: 4
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Preview not available
            </Typography>
            <Typography variant="body2" color="inherit">
              No thumbnail image found for this route
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
